import React, { useState } from 'react';
import styled from 'styled-components';
import GameModeSelector from './GameModeSelector';
import MobileMenu from './MobileMenu';
import { gameModes } from './GameModeVariants';
import YatzyGame from './YatzyGame';
import MultiplayerGame from './MultiplayerGame';
import AdvancedGameModes from './AdvancedGameModes';
import OnlineGameMode from './OnlineGameMode';
import { Player } from '../types/multiplayer';
import { YatzyScores } from '../types/game';
import { MaxiYatzyScores } from '../types/maxi';
import { GameModeConfig } from './GameModeVariants';
import { soundManager } from '../utils/soundManager';
import HelpRulesModal from './HelpRulesModal';
import StartQuickPanel, { type QuickStartTarget } from './StartQuickPanel';
import { loadSavedGame, SavedGame } from '../utils/gameResume';
import { AILevel, AI_LEVEL_NAMES } from '../utils/aiPlayer';
import { useT } from '../i18n';

const GameContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;
`;

const SetupContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  margin: 50px auto;
  
  @media (max-width: 768px) {
    max-width: 90%;
    padding: 24px 20px;
    margin: 20px auto;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    max-width: 95%;
    padding: 28px 22px;
    margin: 20px auto;
    border-radius: 14px;
  }
`;

// CHANGE (2026-02-23): Highscore-preview + snabbstart-panel flyttad till StartQuickPanel.

const Title = styled.h2`
  color: #333;
  font-size: 28px;
  margin-bottom: 30px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    font-size: 26px;
    margin-bottom: 20px;
  }
`;

const PlayerSetup = styled.div`
  margin-bottom: 20px;
`;

const PlayerInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    border-color: #2196F3;
  }
  
  @media (max-width: 480px) {
    padding: 14px;
    font-size: 17px;
    border-radius: 10px;
    margin-bottom: 12px;
  }
`;

const PlayerCount = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 20px;
`;

const CountButton = styled.button<{ active: boolean }>`
  background: ${props => props.active ? '#2196F3' : '#f5f5f5'};
  color: ${props => props.active ? 'white' : '#333'};
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#1976D2' : '#e0e0e0'};
  }
  
  @media (max-width: 480px) {
    padding: 12px 24px;
    font-size: 17px;
    border-radius: 10px;
  }
`;

const StartButton = styled.button`
  background: linear-gradient(145deg, #4CAF50, #45a049);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 15px 30px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  width: 100%;
  margin-top: 20px;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    padding: 18px 32px;
    font-size: 19px;
    border-radius: 14px;
    margin-top: 24px;
  }
`;

// Flytande tärningsbakgrund för startsidan (meny) – täcker hela skärmen och driver i olika riktningar.
// Prestanda: Antalet tärningar reduceras adaptivt (deviceMemory, prefers-reduced-motion, viewport storlek)
// och animationer pausas när fliken är dold.
const FloatingDiceLayer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
  opacity: 0.42;
  mix-blend-mode: overlay;
`;

interface FloatingDieProps { $i: number; $x: number; $y: number; $dur: number; $delay: number; $scale: number; $dx: number; $dy: number; $paused: boolean }

const FloatingDie = styled.span<FloatingDieProps>`
  position: absolute;
  top: ${p => p.$y}%;
  left: ${p => p.$x}%;
  transform: translate(-50%, -50%);
  font-size: clamp(${p => 22 + p.$scale * 14}px, ${p => 4 + p.$scale * 4.4}vw, ${p => 70 + p.$scale * 24}px);
  filter: drop-shadow(0 4px 10px rgba(0,0,0,0.5));
  opacity: .75;
  animation: float-dice-${p => p.$i} ${p => p.$dur}ms linear infinite;
  animation-delay: ${p => -p.$delay}ms;
  animation-play-state: ${p => p.$paused ? 'paused' : 'running'};
  user-select: none;
  pointer-events: none;
  @keyframes float-dice-${p => p.$i} {
    0% { transform: translate(-50%, -50%) translate3d(0,0,0) rotate(0deg) scale(${p => 0.85 + p.$scale * 0.35}); opacity:.0; }
    8% { opacity:.7; }
    50% { transform: translate(-50%, -50%) translate3d(${p => p.$dx}vw, ${p => p.$dy}vh,0) rotate(180deg) scale(${p => 0.95 + p.$scale * 0.4}); opacity:.85; }
    92% { opacity:.7; }
    100% { transform: translate(-50%, -50%) translate3d(0,0,0) rotate(360deg) scale(${p => 0.85 + p.$scale * 0.35}); opacity:0; }
  }
`;

// Liten mute-knapp (FAB) för startsidan - flyttad ned
const MuteFab = styled.button`
  position: fixed;
  top: calc(20px + env(safe-area-inset-top, 0));
  right: 20px;
  z-index: 60;
  background: linear-gradient(145deg,var(--theme-primary,#2563eb),var(--theme-secondary,#1d4ed8));
  border: none;
  color: #fff;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  font-size: 26px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px -4px rgba(0,0,0,0.55), 0 4px 10px -2px rgba(0,0,0,0.4);
  backdrop-filter: blur(8px) saturate(160%);
  transition: transform .25s ease, box-shadow .25s ease;
  &:hover { transform: translateY(-4px); box-shadow:0 10px 26px -6px rgba(0,0,0,0.6),0 6px 14px -2px rgba(0,0,0,0.45); }
  &:active { transform: translateY(-2px) scale(.96); }
  @media (max-width: 640px) { width:52px; height:52px; font-size:24px; }
`;

// CHANGE (2026-02-23): Shortcuts UI flyttad till StartQuickPanel.

// Stor välkomst-tärning
const WelcomeDie = styled.div`
  position:absolute; inset:0; display:flex; align-items:center; justify-content:center; z-index:1;
  pointer-events:none;
  font-size: clamp(140px, 32vw, 340px);
  opacity:.18;
  filter: drop-shadow(0 8px 22px rgba(0,0,0,0.45));
  animation: welcome-pulse 8s ease-in-out infinite;
  @keyframes welcome-pulse {
    0%,100% { transform: scale(1) rotate(0deg); opacity:.18; }
    40% { transform: scale(1.04) rotate(6deg); opacity:.22; }
    70% { transform: scale(0.98) rotate(-4deg); opacity:.17; }
  }
`;

const YatzyGameManager: React.FC = () => {
  const t = useT();
  const [gameMode, setGameMode] = useState<'menu' | 'single' | 'multi' | 'setup' | 'advanced' | 'online'>('menu');
  // Gate bakgrundsmusik till endast aktivt spel (single eller multi)
  React.useEffect(() => {
    if (gameMode === 'single' || gameMode === 'multi') {
      soundManager.playMusic().catch(()=>{});
    } else {
      soundManager.pauseMusic();
    }
  }, [gameMode]);
  const [playerCount, setPlayerCount] = useState(2);
  const [playerNames, setPlayerNames] = useState<string[]>(['', '']);
  // CHANGE (2026-08-15): Valfri AI-motståndare i lokal multiplayer.
  const [aiOpponent, setAiOpponent] = useState<'none' | AILevel>('none');
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedMode, setSelectedMode] = useState<GameModeConfig | null>(null);
  const [multiplayerMode, setMultiplayerMode] = useState<'classic' | 'maxi' | 'forced-classic' | 'forced-maxi'>('classic');
  const [showHelp,setShowHelp] = useState(false);
  const isForcedMultiplayer = multiplayerMode === 'forced-classic' || multiplayerMode === 'forced-maxi';

  // CHANGE (2026-08-15): "Fortsätt spel" + primär "Spela"-knapp på startskärmen.
  const [resumeGame, setResumeGame] = useState(false);
  const [savedGame, setSavedGame] = useState<SavedGame | null>(() => loadSavedGame());
  React.useEffect(() => {
    if (gameMode === 'menu') setSavedGame(loadSavedGame());
  }, [gameMode]);

  const lastSingleModeId = React.useMemo(() => {
    try { return localStorage.getItem('yatzy-last-single-mode'); } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMode]);
  // "Spela"-knappen startar senaste singleplayer-läget (fallback: klassiskt).
  const quickPlayMode = React.useMemo(() => {
    return gameModes.find(m => m.id === lastSingleModeId) || gameModes.find(m => m.id === 'classic') || null;
  }, [lastSingleModeId]);

  const handleResumeSavedGame = () => {
    if (!savedGame) return;
    setSelectedMode(gameModes.find(m => m.id === savedGame.modeId) || null);
    setResumeGame(true);
    setGameMode('single');
  };

  const handleQuickPlay = () => {
    if (!quickPlayMode) return;
    setSelectedMode(quickPlayMode);
    setResumeGame(false);
    setGameMode('single');
  };

  const handleExitSingleToMenu = () => {
    setGameMode('menu');
    setSelectedMode(null);
    setResumeGame(false);
  };

  // CHANGE (2026-02-23): Highscore-preview/state flyttad till StartQuickPanel.

  // URL shortcut handling (?shortcut=daily|advanced|multi|speed|reverse|blitz|best-of-3)
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const sc = params.get('shortcut');
      if (!sc) return;
      if (sc === 'advanced') {
        setGameMode('advanced');
      } else if (sc === 'multi') {
        setGameMode('setup');
      } else if (sc === 'daily') {
        // CHANGE (2026-08-14): daily fanns i kommentaren men hanterades aldrig –
        // notispåminnelser länkade till en oåtkomlig utmaning.
        const dailyMode = gameModes.find(mode => mode.id === 'daily');
        if (dailyMode) {
          setSelectedMode(dailyMode);
          setGameMode('single');
        }
      } else {
        const quickStartModeIds: QuickStartTarget[] = ['speed', 'reverse', 'blitz', 'best-of-3'];
        if ((quickStartModeIds as string[]).includes(sc)) {
          const quickMode = gameModes.find(mode => mode.id === sc);
          if (quickMode) {
            setSelectedMode(quickMode);
            setGameMode('single');
          }
        }
      }
    } catch {}
  }, []);

  const avatars = ['🎯', '🎲', '🎮', '🏆', '⭐', '🎪', '🎨', '🎭'];

  // Prefill names from last used where applicable
  React.useEffect(() => {
    try {
      const last = localStorage.getItem('yatzy-last-name');
      if (last && playerNames[0] === '') {
        setPlayerNames(prev => [last, ...prev.slice(1)]);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModeSelect = (mode: 'single' | 'multi' | 'advanced' | 'online') => {
    if (mode === 'single') {
      setSelectedMode(null);
      setGameMode('single');
    } else if (mode === 'multi') {
      setSelectedMode(null);
      setGameMode('setup');
    } else if (mode === 'advanced') {
      setGameMode('advanced');
    } else if (mode === 'online') {
      setGameMode('online');
    }
  };

  const handleSelectSingleVariant = (variant: 'classic' | 'maxi' | 'forced-classic' | 'forced-maxi' | 'daily') => {
    const m = gameModes.find(m=> m.id===variant);
    if (m) setSelectedMode(m);
    setResumeGame(false);
    setGameMode('single');
  };

  const handleSelectMultiplayerVariant = (variant: 'classic' | 'maxi' | 'forced-classic' | 'forced-maxi') => {
    setMultiplayerMode(variant as any);
    setGameMode('setup');
  };

  // quickStart borttagen (oanvänd) – reducerar ESLint warnings

  const handlePlayerCountChange = (count: number) => {
    setPlayerCount(count);
    setPlayerNames(Array(count).fill('').map((_, i) => playerNames[i] || ''));
  };

  const handlePlayerNameChange = (index: number, name: string) => {
    const newNames = [...playerNames];
    newNames[index] = name;
    setPlayerNames(newNames);
  };

  const handleStartMultiplayer = () => {
    if (playerNames.some(n => n.trim() === '')) return;

    const emptyScores: YatzyScores = {
      ones: null, twos: null, threes: null, fours: null, fives: null, sixes: null,
      onePair: null, twoPairs: null, threeOfAKind: null, fourOfAKind: null,
      smallStraight: null, largeStraight: null, fullHouse: null, chance: null, yatzy: null,
    };
    const emptyMaxiScores: MaxiYatzyScores = {
      ones: null, twos: null, threes: null, fours: null, fives: null, sixes: null,
      onePair: null, twoPairs: null, threeOfAKind: null, fourOfAKind: null, fiveOfAKind: null,
    smallStraight: null, largeStraight: null, fullStraight: null, hus: null, torn: null,
      chance: null, yatzy: null
    };

    const newPlayers: Player[] = playerNames.map((name, index) => ({
      id: `player-${index}`,
      name: name.trim() || `Spelare ${index + 1}`,
      scores: (multiplayerMode === 'maxi' || multiplayerMode === 'forced-maxi') ? { ...emptyMaxiScores } : { ...emptyScores },
      totalScore: 0,
      upperSectionScore: 0,
      upperSectionBonus: 0,
      isActive: index === 0,
      avatar: avatars[index % avatars.length],
      // markera forced genom att lägga till metadata (kan utökas senare):
      modeMeta: { forced: isForcedMultiplayer }
    }));

    // CHANGE (2026-08-15): Lägg till AI-motståndaren sist i turordningen.
    if (aiOpponent !== 'none') {
      newPlayers.push({
        id: 'player-ai',
        name: AI_LEVEL_NAMES[aiOpponent],
        scores: (multiplayerMode === 'maxi' || multiplayerMode === 'forced-maxi') ? { ...emptyMaxiScores } : { ...emptyScores },
        totalScore: 0,
        upperSectionScore: 0,
        upperSectionBonus: 0,
        isActive: false,
        avatar: '🤖',
        modeMeta: { forced: isForcedMultiplayer },
        isAI: true,
        aiLevel: aiOpponent,
      });
    }

    setPlayers(newPlayers);
    setGameMode('multi');
  };

  const handleGameEnd = () => {
    setGameMode('menu');
    setPlayers([]);
    setPlayerNames(['', '']);
    setPlayerCount(2);
  };

  // (Tidigare hem-knapp borttagen enligt önskemål)

  // Adaptivt antal tärningar + paus-state (måste deklareras innan returns)
  const [dicePaused, setDicePaused] = React.useState<boolean>(document.visibilityState === 'hidden');
  const diceCount = React.useMemo(() => {
    let base = 26;
    const mem: any = (navigator as any).deviceMemory;
    if (mem && mem <= 4) base = 18;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) base = 10;
    if (window.innerWidth < 500) base -= 4;
    if (window.innerHeight < 640) base -= 2;
    return Math.max(8, base);
  }, []);
  React.useEffect(() => {
    const vis = () => setDicePaused(document.visibilityState === 'hidden');
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const motion = () => setDicePaused(mql.matches ? true : document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', vis);
    mql.addEventListener('change', motion);
    return () => { document.removeEventListener('visibilitychange', vis); mql.removeEventListener('change', motion); };
  }, []);

  // Mute-knapp state måste ligga innan tidiga returns (hooks regler)
  const [muted,setMuted] = React.useState(()=> !soundManager.getSettings().enabled);
  const toggleMute = () => {
    const newMuted = !muted;
    setMuted(newMuted);
    soundManager.setEnabled(!newMuted);
    soundManager.setMusicEnabled(!newMuted);
  };

  if (gameMode === 'single') {
    return <>
      <YatzyGame mode={selectedMode || undefined} resume={resumeGame} onExit={handleExitSingleToMenu} />
    </>;
  }

  if (gameMode === 'multi') {
    return <>
      <MultiplayerGame players={players} onGameEnd={handleGameEnd} />
    </>;
  }

  if (gameMode === 'online') {
    return (
      <GameContainer>
        <OnlineGameMode onExitToMenu={() => setGameMode('menu')} />
      </GameContainer>
    );
  }

  if (gameMode === 'advanced') {
    return (
      <GameContainer>
        <AdvancedGameModes 
          onBackToMenu={() => setGameMode('menu')}
          onStartGame={(mode: GameModeConfig) => {
            setSelectedMode(mode);
            setResumeGame(false);
            setGameMode('single');
          }}
        />
        <MobileMenu isOpen={false} onClose={()=>{}} onHome={()=> setGameMode('menu')} activeGame={false} />
      </GameContainer>
    );
  }

  if (gameMode === 'setup') {
    return (
      <GameContainer>
        <SetupContainer>
          
          <Title>{t('setup.title')}</Title>

          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <h4>{t('setup.mode')}</h4>
            <PlayerCount>
        {(['classic','maxi','forced-classic','forced-maxi'] as const).map(mode => (
                <CountButton
                  key={mode}
                  active={multiplayerMode === mode}
                  onClick={() => setMultiplayerMode(mode)}
                >
          {mode === 'classic' ? t('setup.mode.classic') : mode === 'maxi' ? t('setup.mode.maxi') : mode === 'forced-classic' ? t('setup.mode.forced') : t('setup.mode.forcedMaxi')}
                </CountButton>
              ))}
            </PlayerCount>
            <p style={{ margin: '8px 0 16px', opacity: 0.85, fontSize: 13 }}>
              {t('setup.modeNote')}
            </p>

            <h4>{t('setup.ai')}</h4>
            <PlayerCount>
              {(['none', 'easy', 'medium', 'hard'] as const).map(level => (
                <CountButton
                  key={level}
                  active={aiOpponent === level}
                  onClick={() => {
                    setAiOpponent(level);
                    if (level === 'none' && playerCount === 1) handlePlayerCountChange(2);
                  }}
                >
                  {t(`setup.ai.${level}`)}
                </CountButton>
              ))}
            </PlayerCount>
            <p style={{ margin: '8px 0 16px', opacity: 0.85, fontSize: 13 }}>
              {t('setup.aiNote')}
            </p>

            <h4>{t('setup.playerCount')}</h4>
            <PlayerCount>
              {[1, 2, 3, 4, 5, 6].map(count => (
                <CountButton
                  key={count}
                  active={playerCount === count}
                  onClick={() => {
                    handlePlayerCountChange(count);
                    // CHANGE (2026-08-15): "1 spelare" var tidigare inaktiverad tills
                    // en AI valts – men såg identisk ut, så den upplevdes trasig.
                    // Nu aktiveras en AI-motståndare automatiskt i stället, så att
                    // "spela ensam mot AI" fungerar oavsett i vilken ordning man väljer.
                    if (count === 1 && aiOpponent === 'none') setAiOpponent('medium');
                  }}
                >
                  {count === 1 ? t('setup.soloVsAi') : count}
                </CountButton>
              ))}
            </PlayerCount>
            <p style={{ margin: '8px 0 0', opacity: 0.85, fontSize: 13 }}>
              {t('setup.soloNote')}
            </p>
          </div>

            <div>
              <h4>{t('setup.playerNames')}</h4>
              {Array(playerCount).fill(0).map((_, index) => (
                <PlayerSetup key={index}>
                  <PlayerInput
                    type="text"
                    placeholder={`${avatars[index % avatars.length]} ${t('setup.playerPlaceholder', { n: index + 1 })}`}
                    value={playerNames[index] || ''}
                    onChange={(e) => handlePlayerNameChange(index, e.target.value)}
                    maxLength={20}
                  />
                </PlayerSetup>
              ))}
            </div>

            <StartButton
              onClick={handleStartMultiplayer}
              disabled={playerNames.some(n => n.trim() === '')}
            >
              {t('setup.start')}
            </StartButton>
        </SetupContainer>
  </GameContainer>
    );
  }

  return (
  <GameContainer style={{ position: 'relative', padding: 'calc(env(safe-area-inset-top,0) + clamp(40px,8vh,80px)) 20px 40px' }}>
      {/* Flytande tärningar bakgrund */}
      <FloatingDiceLayer>
        {(() => {
          return Array.from({ length: diceCount }).map((_, i) => {
            const faces = ['🎲','⚂','⚅','⚄','⚃','⚁'];
            const dur = 18000 + Math.random()*20000; // 18–38s
            const delay = Math.random()*dur;
            const x = Math.random()*100; // start X percentage
            const y = Math.random()*100; // start Y percentage
            const scale = Math.random();
            const dx = (Math.random()*36 - 18); // -18vw to +18vw drift
            const dy = (Math.random()*48 - 24); // -24vh to +24vh drift
            return (
              <FloatingDie
                key={i}
                $i={i}
                $x={x}
                $y={y}
                $dur={dur}
                $delay={delay}
                $scale={scale}
                $dx={dx}
                $dy={dy}
                $paused={dicePaused}
              >
                {faces[i % faces.length]}
              </FloatingDie>
            );
          });
        })()}
      </FloatingDiceLayer>
  <WelcomeDie aria-hidden="true">🎲</WelcomeDie>
      <MuteFab data-tip={muted? 'Slå på ljud':'Stäng av ljud'} aria-label={muted? 'Slå på ljud':'Stäng av ljud'} onClick={toggleMute}>
        {muted? '🔇':'🔊'}
      </MuteFab>
  <div style={{ position: 'relative', zIndex: 2 }}>
        <GameModeSelector
          onModeSelect={handleModeSelect}
          onSelectSingleVariant={handleSelectSingleVariant}
          onSelectMultiplayerVariant={handleSelectMultiplayerVariant}
          onHome={()=> {/* already home */}}
          resumeInfo={savedGame ? {
            modeName: gameModes.find(m => m.id === savedGame.modeId)?.name || 'Yatzy',
            round: savedGame.gameState.currentRound,
          } : null}
          onResume={handleResumeSavedGame}
          quickPlayModeName={quickPlayMode?.name || null}
          onQuickPlay={handleQuickPlay}
        />

        {/* CHANGE (2026-08-15): ⚡-snabbstartsmenyn borttagen (dubblerade Avancerade
            lägen) – panelen visar nu bara highscore-previewn. */}
        <StartQuickPanel modes={gameModes} />

        <div style={{marginTop: 30, display:'flex', justifyContent:'center'}}>
          <button onClick={()=> setShowHelp(true)} aria-label="Hjälp / Regler" style={{
            background:'linear-gradient(135deg,var(--theme-primary,#2563eb),var(--theme-secondary,#1d4ed8))',
            color:'#fff', border:'none', padding:'14px 28px', borderRadius:18, fontSize:16, fontWeight:600,
            cursor:'pointer', boxShadow:'0 6px 18px -4px rgba(0,0,0,0.5)', letterSpacing:'.4px'
          }}>{t('start.help')}</button>
        </div>
      </div>
      {/** Ljud & tema kontroller dolda här enligt önskemål */}
  <MobileMenu isOpen={false} onClose={()=>{}} onHome={()=> setGameMode('menu')} activeGame={false} />
  <HelpRulesModal open={showHelp} onClose={()=> setShowHelp(false)} />
    </GameContainer>
  );
};

// Home navigation helper (used by home button & brand)
// useHomeNavigation borttagen (oanvänd) – reducerar ESLint warnings

export default YatzyGameManager;
